import { generateExam, getExamPaperById, regenerateExam } from "src/api/exam"
import React, { useEffect, useRef, useState } from "react"
import {
  FiClipboard,
  FiMenu,
  FiPenTool,
  FiRepeat,
  FiSave,
} from "react-icons/fi"
import { useParams } from "react-router-dom"
import Button from "src/component/button/button"
import Header from "src/component/header/header"
import HierarchyPathDisplay from "src/components/HierarchyPathDisplay"
import { errorMessageShow, LoaderComponent } from "src/utils/Helper"
import { hierarchyTopicKey } from "src/utils/hierarchyTopic"
import SaveExamModal from "./SaveExamModel"

const UseTemplate = () => {
  const [generatedQuestion, setGenerateQuestion] = useState<any>({})
  const contentRef = useRef<HTMLDivElement>(null)
  const [isMathJaxReady, setIsMathJaxReady] = useState(false)
  const { templateId, examId } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [saveButtonText, setSaveButtonText] = useState("Save")
  const [examDetail, setExamDetail] = useState({})
  useEffect(() => {
    if (templateId) {
      setIsLoading(true)
      handleGenerateExam()
    }
  }, [templateId])

  useEffect(() => {
    if (examId) {
      setIsLoading(true)
      handleGetExamPaperById(examId)
    }
  }, [examId])

  const handleGetExamPaperById = (examId: string) => {
    getExamPaperById(examId)
      .then((res: any) => {
        setGenerateQuestion({ topic_groups: res?.data?.topic_groups })
        setExamDetail(res?.data)
      })
      .catch((er) => {
        console.log({ er })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleGenerateExam = async () => {
    try {
      const data = await generateExam(templateId ?? "")
      setGenerateQuestion(data?.data?.exam_data)
    } catch (err: any) {
      const message = err?.response?.data?.message ?? "Something went wrong!"
      errorMessageShow(message)
    } finally {
      setIsLoading(false)
    }
  }

  const typesetMathJax = () => {
    if (window.MathJax) {
      const contentElement = contentRef.current
      if (contentElement) {
        window.MathJax.typesetPromise([contentElement])
          .then(() => {
            setIsMathJaxReady(true)
          })
          .catch((err: any) => console.error("MathJax Render Error:", err))
      }
    } else {
      console.error("MathJax is not loaded")
    }
  }

  useEffect(() => {
    if (!window.MathJax)
      return console.error("this is data MathJax is not loaded yet.")
    setTimeout(typesetMathJax, 100)
  })

  let questionCounter = 1

  const cleanHTML = (html: string = "") => {
    return html
      .replace(/<p[^>]*>(&nbsp;|\s)*<\/p>/g, "")
      .replace(/<div>\s*(<span class="katex">[\s\S]*?)<\/div>/g, "$1")
  }

  const updateQuestionInState = (
    updatedQuestion: any,
    topicSlot: any,
    previousQuestion_id: string
  ) => {
    const topicKey = hierarchyTopicKey(topicSlot)
    setGenerateQuestion((prev: any) => {
      if (!prev?.topic_groups) return prev

      const newState = { ...prev }

      newState.topic_groups = newState.topic_groups.map((group: any) => ({
        ...group,
        topics: group.topics.map((t: any) => {
          if (hierarchyTopicKey(t) === topicKey) {
            return {
              ...t,
              questions: t.questions.map((q: any) =>
                q.question_id === previousQuestion_id ? updatedQuestion : q
              ),
            }
          }
          return t
        }),
      }))

      return newState
    })
  }
  return (
    <div className='content templates flex gap-[16px]'>
      <Header
        pageTitle='Mathematics for Pros'
        buttonText='Publish'
        buttonIcon={<FiClipboard />}
        onClick={() => {
          setSaveButtonText("Publish")
          setIsSaveModalOpen(true)
        }}
        extraButton={
          <>
            <Button
              style={{ marginLeft: "5px" }}
              type='primary'
              buttonType='button'
              icon={<FiSave />}
              text='Save'
              onClick={() => {
                setSaveButtonText("Save")
                setIsSaveModalOpen(true)
              }}
            />
          </>
        }
      />

      {isLoading ? (
        <LoaderComponent
          label={
            examId ? "Loading exam…" : "Generating exam from template…"
          }
        />
      ) : (
        <div ref={contentRef}>
          {generatedQuestion?.topic_groups?.map((item: any, index: number) => {
            return (
              <div
                className='box-shadow px-[40px] py-[70px] rounded-[4px] sm:w-full lg:w-[80%] flex flex-col gap-[24px]'
                key={index}
              >
                <h2 className='text-[20px] font-semibold text-gray-800'>
                  {item?.name}
                </h2>

                {item?.topics?.map((topic: any) => {
                  const topicKey = hierarchyTopicKey(topic)
                  return (
                    <React.Fragment key={topicKey}>
                      <div className="px-2 pb-2">
                        <HierarchyPathDisplay topic={topic} variant="rows" />
                      </div>
                      {topic?.questions.map((question: any) => {
                        const questionIndex = questionCounter++
                        return (
                          <SingleQuestion
                            key={question?.question_id}
                            question={question}
                            topic={topic}
                            cleanHTML={cleanHTML}
                            questionIndex={questionIndex}
                            updateQuestionInState={updateQuestionInState}
                          />
                        )
                      })}
                    </React.Fragment>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}

      <SaveExamModal
        isOpen={isSaveModalOpen}
        examDetail={examDetail}
        onClose={() => setIsSaveModalOpen(false)}
        loading={isLoading}
        buttonText={saveButtonText}
        subjects={[
          { label: "Math", value: "math" },
          { label: "Science", value: "science" },
        ]}
        topic_group={generatedQuestion?.topic_groups?.map((group: any) => ({
          name: group.name,
          topics: group.topics.map((topic: any) => ({
            name: topic.name,
            topic_id: topic.topic_id,
            topic: topic.topic,
            level_2_id: topic.level_2_id,
            level_2_name: topic.level_2_name,
            level_3_id: topic.level_3_id,
            level_3_name: topic.level_3_name,
            level_4_id: topic.level_4_id,
            level_4_name: topic.level_4_name,
            questions: topic.questions.map((q: any) => ({
              question_id: q.question_id,
              complexity: q.complexity || null,
              marks: q.number || null,
            })),
          })),
        }))}
      />
    </div>
  )
}

export default UseTemplate

const SingleQuestion = ({
  question,
  topic,
  cleanHTML,
  questionIndex,
  updateQuestionInState,
}: any) => {
  const [activeTab, setActiveTab] = useState<
    "question" | "solution" | "answer"
  >("question")
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleRegenerate = async () => {
    if (!question?.question_id) return

    setIsRegenerating(true)
    setErrorMessage(null)

    try {
      const res = await regenerateExam(question.question_id, [])
      const question_data = res?.data?.question_data

      if (!question_data) {
        setErrorMessage("Failed to fetch regenerated question.")
      } else {
        updateQuestionInState(question_data, topic, question.question_id)
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        "Something went wrong while regenerating."
      setErrorMessage(message)
      console.error({ err })
    } finally {
      setIsRegenerating(false)
    }
  }

  return (
    <div className='flex flex-col justify-center items-start p-[12px] gap-2 self-stretch rounded-[11px] border border-[#F9FAFB] bg-[#f1f3f5]'>
      <div className='flex justify-between items-center border-b px-4 py-3 bg-[#f1f3f5] w-full'>
        <div className='flex gap-2 items-center'>
          <FiMenu color='var(--gray-400)' fontSize={22} />
          <span className='text-gray-700 text-[20px] px-2 py-[2px] rounded flex'>
            {questionIndex}
          </span>
          <span className='rounded-[4px] border border-[#98A2B3] bg-[#FCFCFD] px-[8px] py-[2px] text-[#1D2939] text-[12px] font-normal leading-[18px]'>
            <HierarchyPathDisplay topic={topic} />
          </span>
          <span className='rounded-[4px] border border-[#98A2B3] bg-[#FCFCFD] px-[8px] py-[2px] text-[#1D2939] text-[12px] font-normal leading-[18px]'>
            {question?.complexity}
          </span>
        </div>

        <div className='flex items-center gap-3'>
          <div className='rounded-[4px] border border-[#E4E7EC] bg-[#E4E7EC] flex items-center gap-[8px] p-[1px]'>
            {["Question", "Solution", "Answer"].map((item: any) => (
              <button
                key={item}
                onClick={() => setActiveTab(item.toLowerCase())}
                className={`flex items-center justify-center gap-[8px] px-[13px] py-[4px] font-plus-jakarta text-[16px] leading-[24px] ${
                  activeTab === item.toLowerCase()
                    ? "bg-white text-[#1D2939] font-semibold"
                    : "bg-transparent text-[#667085] font-medium"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className={`flex items-center justify-center gap-[8px] px-[16px] py-[10px] rounded-[8px] border border-[#667085] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] text-[var(--Gray-500,#667085)] font-medium text-sm leading-[20px] font-sans ${
              isRegenerating ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            <FiRepeat
              size={20}
              className={isRegenerating ? "animate-spin" : ""}
            />
            {isRegenerating ? "Regenerating..." : "Regenerate"}
          </button>
        </div>
      </div>

      {errorMessage && (
        <p className='text-red-500 text-sm px-4 py-2'>{errorMessage}</p>
      )}

      <div
        className='flex flex-col items-start p-[12px] self-stretch rounded-lg bg-[var(--White,#FFF)]'
        dangerouslySetInnerHTML={{ __html: cleanHTML(question[activeTab]) }}
      />
    </div>
  )
}
